package com.damda.design_system.ui.views.lists

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.FloatingActionButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.damda.design_system.core.ImageResourceLoader
import com.damda.design_system.ui.views.images.LoadAndDisplayImage
import kotlinx.serialization.Serializable

@Composable
fun FoodItemCard(
    imageResourceLoader: ImageResourceLoader,
    foodItem: FoodItem,
    onAddToCartClick: () -> Unit,
    onCardClick: (FoodItem) -> Unit,
    cardWidth: Dp = 190.dp,
    modifier: Modifier = Modifier
) {
    Card(
        shape = RoundedCornerShape(16.dp),
        modifier = modifier
            .width(cardWidth)
            .wrapContentHeight(),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 4.dp),
        onClick = { onCardClick.invoke(foodItem) }
    ) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(145.dp)  // 이미지 높이 고정
            ) {
                LoadAndDisplayImage(
                    imageResourceLoader = imageResourceLoader,
                    path = foodItem.imageName,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier
                        .fillMaxSize()
                        .clip(RoundedCornerShape(13.dp))
                )
            }
            Box(modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.fillMaxWidth().fillMaxHeight().padding(top = 10.dp, bottom = 22.dp)) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = foodItem.title,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1,
                        color = Color(0xFF333333),
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = foodItem.subtitle,
                        fontSize = 10.sp,
                        color = Color(0xFFB0B0B0),
                        maxLines = 1,
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                    Spacer(modifier = Modifier.height(10.dp))
                    Text(
                        text = foodItem.price,
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1,
                        color = Color(0xFF333333),
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }
                FloatingActionButton(
                    onClick = { /* Handle FAB click */ },
                    containerColor = Color(0xFFFFA726),
                    contentColor = Color.White,
                    elevation = FloatingActionButtonDefaults.elevation(8.dp),
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                        .size(32.dp)
                        .padding(18.dp)
                ) {
                    Icon(Icons.Filled.Add, contentDescription = "Add")
                }
            }
        }
    }
}

@Serializable
data class FoodItem(
    val imageName: String,
    val title: String,
    val subtitle: String,
    val price: String,
    val detail: String,
    val ingredients: List<String>
)

@Composable
fun FoodList(
    imageResourceLoader: ImageResourceLoader,
    items: List<FoodItem>,
    onAddToCartClick: (FoodItem) -> Unit,
    onCardClick: (FoodItem) -> Unit,
    modifier: Modifier = Modifier
) {
    LazyRow(
        modifier = modifier
            .fillMaxWidth(),
        contentPadding = PaddingValues(horizontal = 30.dp), // 초기 horizontal 패딩값 설정
        horizontalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        items(items.size) { index ->
            val item = items[index]
            FoodItemCard(
                imageResourceLoader = imageResourceLoader,
                foodItem = item,
                onAddToCartClick = { onAddToCartClick(item) },
                onCardClick = onCardClick
            )
        }
    }
}

val foodItems = listOf(
    FoodItem(
        imageName = "f_jamon_nade.jpeg",
        title = "하몽네이드(jamon nade)",
        subtitle = "하몽과 마리네이드를 합친 새콤한 조합의 핀초",
        price = "4,000원",
        detail = "🔸 핀초 메뉴: 하몽과 마리네이드를 합친 새콤한 조합 🔸\n\n" +
                "⭐ 이름: 하몽 마리네이드 핀초 ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 특별한 핀초는 스페인의 대표적인 생햄인 하몽과 상큼한 마리네이드를 결합한 요리입니다. 하몽의 깊고 풍부한 맛이 새콤달콤한 마리네이드와 어우러져 절묘한 조화를 이룹니다. 바삭한 바게트 위에 올려져, 한 입 베어물 때마다 다양한 식감과 풍미를 느낄 수 있습니다. 바삭한 감자칩과 함께 제공되어 더욱 다채로운 맛을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 하몽 (스페인산 생햄)\n" +
                "• 마리네이드 (레몬즙, 올리브 오일, 다진 마늘, 다진 양파, 허브)\n" +
                "• 바게트 슬라이스\n" +
                "• 신선한 토마토 다이스\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 하몽의 짭조름하고 감칠맛 나는 풍미\n" +
                "• 마리네이드의 상큼하고 상쾌한 맛\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 신선한 토마토와 바질이 더해진 상쾌함\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 시원한 화이트 와인 또는 스파클링 와인\n" +
                "• 와인 맥주\n\n" +
                "이 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 하몽 마리네이드 핀초를 꼭 맛보세요!",
        ingredients = listOf("하몽", "마리네이드", "신선한 토마토 다이스", "나초칩")
    ),
    FoodItem(
        imageName = "f_carbonara_pasta.jpeg",
        title = "까르보나라 파스타",
        subtitle = "남녀노소 누구나 맛있게 먹을수 있는 정통 까르보나라\n",
        price = "17,000원",
        detail = "🔸 파스타 메뉴: 클래식 까르보나라 🔸\n\n" +
                "⭐ 이름: 클래식 까르보나라 ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 까르보나라는 정통 이탈리아 방식으로 만들어진 클래식 파스타 요리입니다. 크리미한 소스와 짭조름한 베이컨의 조화가 일품인 이 요리는, 신선한 계란과 파마산 치즈가 주는 풍부한 맛이 특징입니다. 알 덴테로 완벽하게 삶아진 스파게티 면이 소스와 완벽하게 어우러져, 부드러우면서도 고소한 풍미를 느낄 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 스파게티 면\n" +
                "• 베이컨 또는 판체타\n" +
                "• 신선한 계란\n" +
                "• 파마산 치즈\n" +
                "• 다진 마늘\n" +
                "• 올리브 오일\n" +
                "• 소금 및 후추\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 크리미하고 고소한 소스\n" +
                "• 베이컨의 짭조름하고 풍부한 맛\n" +
                "• 알 덴테로 삶아진 스파게티의 완벽한 식감\n" +
                "• 파마산 치즈의 깊은 풍미\n" +
                "• 파슬리의 상쾌한 향 (옵션)\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 가벼운 레드 와인 또는 화이트 와인\n" +
                "이 클래식 까르보나라는 간단하지만 정통의 맛을 그대로 담아낸 요리로, 특별한 날에도, 일상 속에서도 언제든 즐기기 좋은 메뉴입니다. 신선한 재료와 정성스런 조리로 만들어진 까르보나라 파스타를 꼭 맛보세요!",
        ingredients = listOf("스파게티", "베이컨", "계란", "치즈", "마늘", "후추", "올리브 오일")
    ),
    FoodItem(
        imageName = "f_mozza_prawn.jpeg",
        title = "모짜르트새우(mozza prawn)",
        subtitle = "버터새우와 베이컨 핀초",
        price = "4,200원",
        detail = "🔸 핀초 메뉴: 모짜르트새우 🔸\n\n" +
                "⭐ 이름: 모짜르트새우 (Mozza Prawn) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 모짜르트새우 핀초는 신선한 새우와 고소한 모짜렐라 치즈의 조화가 일품인 요리입니다. 바삭한 바게트 위에 올려진 새우와 치즈는 한 입 베어물 때마다 바삭함과 쫄깃함을 동시에 느낄 수 있습니다. 그 위에 더해진 베이컨과 허브는 풍미를 더욱 풍부하게 만들어 줍니다. 바삭한 감자칩과 함께 제공되어 더욱 다채로운 맛을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 신선한 새우\n" +
                "• 모짜렐라 치즈\n" +
                "• 바게트 슬라이스\n" +
                "• 베이컨\n" +
                "• 허브\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 신선한 새우의 담백한 맛\n" +
                "• 모짜렐라 치즈의 쫄깃하고 고소한 풍미\n" +
                "• 베이컨의 짭조름하고 풍부한 맛\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 허브의 상쾌한 향\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "이 모짜르트새우 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 모짜르트새우 핀초를 꼭 맛보세요!",
        ingredients = listOf("신선한 새우", "모짜렐라 치즈", "베이컨")
    ),
    FoodItem(
        imageName = "f_salmon_peper.jpeg",
        title = "연어페퍼(salmon peper)",
        subtitle = "크림치즈 사이에 연어를 넣어 체리페퍼를 올린 핀초",
        price = "5,000원",
        detail = "🔸 핀초 메뉴: 연어페퍼 🔸\n\n" +
                "⭐ 이름: 연어페퍼 (Salmon Pepper) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 연어페퍼 핀초는 신선한 훈제 연어와 달콤한 구운 피망이 어우러진 요리입니다. 바삭한 바게트 위에 크림치즈를 얹고, 그 위에 신선한 훈제 연어와 구운 피망을 올려 풍미를 더했습니다. 발사믹 글레이즈가 살짝 뿌려져, 새콤달콤한 맛이 조화를 이루며, 바삭한 감자칩과 함께 제공되어 다채로운 식감을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 신선한 훈제 연어\n" +
                "• 구운 피망\n" +
                "• 크림치즈\n" +
                "• 바게트 슬라이스\n" +
                "• 로즈마리\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 훈제 연어의 부드럽고 풍부한 맛\n" +
                "• 구운 피망의 달콤한 맛\n" +
                "• 크림치즈의 고소하고 크리미한 식감\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 발사믹 글레이즈의 새콤달콤한 맛\n" +
                "• 로즈마리의 향긋한 향\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 시원한 화이트 와인 또는 로제 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 연어페퍼 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 연어페퍼 핀초를 꼭 맛보세요!",
        ingredients = listOf("신선한 훈제 연어", "구운 피망", "크림치즈")
    ),
    FoodItem(
        imageName = "f_sauba_ricotta.jpeg",
        title = "소베리코타(sauba ricotta)",
        subtitle = "크림치즈 사이에 연어를 넣어 체리페퍼를 올린 핀초",
        price = "4,500원",
        detail = "🔸 핀초 메뉴: 소베리코타 🔸\n\n" +
                "⭐ 이름: 소베리코타 (Sauba Ricotta) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 소베리코타 핀초는 풍부한 맛을 자랑하는 요리로, 신선한 리코타 치즈와 소고기 베이컨이 어우러진 특별한 맛을 제공합니다. 바삭한 바게트 위에 크리미한 리코타 치즈와 짭조름한 소고기 베이컨을 얹고, 발사믹 글레이즈와 허브로 마무리하여 풍미를 더했습니다. 바삭한 감자칩과 함께 제공되어 다채로운 식감을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 소고기 베이컨\n" +
                "• 리코타 치즈\n" +
                "• 바게트 슬라이스\n" +
                "• 발사믹 글레이즈\n" +
                "• 신선한 허브 (바질 등)\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 소고기 베이컨의 짭조름하고 깊은 맛\n" +
                "• 리코타 치즈의 부드럽고 크리미한 식감\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 발사믹 글레이즈의 새콤달콤한 맛\n" +
                "• 허브의 상쾌한 향\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 진한 레드 와인 또는 가벼운 화이트 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 소베리코타 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 소베리코타 핀초를 꼭 맛보세요!",
        ingredients = listOf("베이컨", "리코타 치즈")
    ),
    FoodItem(
        imageName = "f_chorizogambas.jpeg",
        title = "초리소바스(chorizogambas)",
        subtitle = "초리소와 감바스핀초",
        price = "4,500원",
        detail = "🔸 핀초 메뉴: 초리소바스 🔸\n\n" +
                "⭐ 이름: 초리소바스 (Chorizo Gambas) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 초리소바스 핀초는 매콤한 초리소 소시지와 신선한 새우가 어우러진 요리입니다. 바삭한 바게트 위에 올려진 초리소와 새우는 한 입 베어물 때마다 다양한 풍미를 느낄 수 있습니다. 볶은 양파와 허브로 맛을 더해 깊고 풍부한 맛을 제공합니다. 바삭한 감자칩과 함께 제공되어 다채로운 식감을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 초리소 소시지\n" +
                "• 신선한 새우\n" +
                "• 바게트 슬라이스\n" +
                "• 볶은 양파\n" +
                "• 허브 (파슬리 등)\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 초리소의 매콤하고 풍부한 맛\n" +
                "• 새우의 담백하고 신선한 맛\n" +
                "• 볶은 양파의 달콤한 맛\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 허브의 상쾌한 향\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 진한 레드 와인 또는 가벼운 화이트 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 초리소바스 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 초리소바스 핀초를 꼭 맛보세요!",
        ingredients = listOf("소시지", "신선한 새우", "볶은 양파")
    ),
    FoodItem(
        imageName = "f_caramel_bb.jpeg",
        title = "카라멜 바베큐(caramel bbq)",
        subtitle = "카라멜어니언과 고기를 올린 담백한 핀초",
        price = "5,500원",
        detail = "🔸 핀초 메뉴: 카라멜 바베큐 🔸\n\n" +
                "⭐ 이름: 카라멜 바베큐 (Caramel BBQ) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 카라멜 바베큐 핀초는 달콤하고 짭조름한 맛이 조화를 이루는 요리입니다. 바삭한 바게트 위에 풍부한 카라멜 바베큐 소스로 조리된 고기와 볶은 양파를 얹고, 그 위에 신선한 치즈를 갈아 올려 완성했습니다. 바삭한 감자칩과 함께 제공되어 다양한 식감을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 카라멜 바베큐 소스에 조리된 고기\n" +
                "• 볶은 양파\n" +
                "• 바게트 슬라이스\n" +
                "• 갈아낸 신선한 치즈\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 달콤하고 짭조름한 카라멜 바베큐 소스의 풍미\n" +
                "• 부드럽고 풍부한 고기의 맛\n" +
                "• 볶은 양파의 달콤한 맛\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 신선한 치즈의 고소함\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 진한 레드 와인 또는 가벼운 화이트 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 카라멜 바베큐 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 카라멜 바베큐 핀초를 꼭 맛보세요!",
        ingredients = listOf("카라멜 바베큐 소스에 조리된 고기", "볶은 양파", "갈아낸 신선한 치즈")
    ),
    FoodItem(
        imageName = "f_salmon_nade.jpeg",
        title = "연어네이드(salmon nade)",
        subtitle = "아낌없이 올린 연어핀초",
        price = "5,200원",
        detail = "🔸 핀초 메뉴: 연어네이드 🔸\n\n" +
                "⭐ 이름: 연어네이드 (Salmon Nade) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 연어네이드 핀초는 신선한 훈제 연어와 풍부한 양념이 어우러진 특별한 요리입니다. 바삭한 바게트 위에 올려진 연어는 상큼한 양념과 함께 조화를 이루어 깊고 풍부한 맛을 제공합니다. 바삭한 감자칩과 함께 제공되어 다양한 식감을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 신선한 훈제 연어\n" +
                "• 특제 양념\n" +
                "• 바게트 슬라이스\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 신선한 연어의 부드럽고 담백한 맛\n" +
                "• 특제 양념의 상큼하고 풍부한 맛\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 시원한 화이트 와인 또는 로제 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 연어네이드 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 연어네이드 핀초를 꼭 맛보세요!",
        ingredients = listOf("신선한 훈제 연어", "특제 양념")
    ),
    FoodItem(
        imageName = "f_egg_salami.jpeg",
        title = "에그살라미(egg salami)",
        subtitle = "계란과 감자를 섞어만든 핀초",
        price = "4,500원",
        detail = "🔸 핀초 메뉴: 에그살라미 🔸\n\n" +
                "⭐ 이름: 에그살라미 (Egg Salami) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 에그살라미 핀초는 고소한 살라미와 부드러운 달걀이 조화롭게 어우러진 요리입니다. 바삭한 바게트 위에 크리미한 달걀 샐러드와 짭짤한 살라미를 올리고, 신선한 토마토와 루꼴라로 마무리하여 상큼함을 더했습니다. 바삭한 감자칩과 함께 제공되어 다양한 식감을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 고소한 살라미\n" +
                "• 크리미한 달걀 샐러드\n" +
                "• 신선한 토마토\n" +
                "• 루꼴라\n" +
                "• 바게트 슬라이스\n" +
                "• 바삭한 나초칩\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 살라미의 짭짤하고 풍부한 맛\n" +
                "• 달걀 샐러드의 부드럽고 크리미한 식감\n" +
                "• 신선한 토마토의 상큼한 맛\n" +
                "• 루꼴라의 향긋한 풍미\n" +
                "• 바게트의 바삭한 식감\n" +
                "• 감자칩의 바삭함이 추가된 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 시원한 화이트 와인 또는 로제 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 에그살라미 핀초는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 에그살라미 핀초를 꼭 맛보세요!",
        ingredients = listOf("고소한 살라미", "크리미한 달걀 샐러드", "신선한 토마토")
    ),
    FoodItem(
        imageName = "f_patatas_bravas.jpeg",
        title = "파타타스브라바스",
        subtitle = "구운감자위에 알리올리소스와 매콤한 소스를 올린요리",
        price = "12,000원",
        detail = "🔸 메인 메뉴: 파타타스 브라바스 🔸\n\n" +
                "⭐ 이름: 파타타스 브라바스 (Patatas Bravas) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 파타타스 브라바스는 스페인의 대표적인 타파스 요리로, 바삭하게 구운 감자 위에 크리미한 알리올리 소스와 매콤한 브라바스 소스를 얹어 완성한 요리입니다. 감자의 바삭한 식감과 두 가지 소스의 조화가 일품으로, 간단하면서도 풍부한 맛을 즐길 수 있습니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 바삭하게 구운 감자\n" +
                "• 매콤한 브라바스 소스\n" +
                "• 신선한 바질 잎\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 바삭한 감자의 식감\n" +
                "• 크리미한 알리올리 소스의 부드러운 맛\n" +
                "• 매콤한 브라바스 소스의 강렬한 맛\n" +
                "• 바질의 향긋한 풍미\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 시원한 맥주 또는 가벼운 레드 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 파타타스 브라바스는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 식사로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 파타타스 브라바스를 꼭 맛보세요!",
        ingredients = listOf("바삭하게 구운 감자", "매콤한 브라바스 소스", "신선한 바질 잎")
    ),
    FoodItem(
        imageName = "f_padron_pepers.jpeg",
        title = "파드론페퍼(padron pepers)",
        subtitle = "스페인 대표 술안주",
        price = "FREE",
        detail =  "🔸 서비스 메뉴: 파드론 페퍼 🔸\n\n" +
                "⭐ 이름: 파드론 페퍼 (Padrón Peppers) ⭐\n\n" +
                "🔹 설명: 🔹\n" +
                "이 파드론 페퍼는 스페인의 전통적인 타파스 요리로, 고소하고 짭짤한 맛이 특징입니다. 신선한 파드론 페퍼를 올리브 오일에 바삭하게 구워 소금으로 간을 하여 간단하지만 깊은 풍미를 자랑합니다. 약간의 매운 맛이 가미된 파드론 페퍼는 맥주나 와인과 함께 즐기기에 완벽한 안주입니다.\n\n" +
                "🔹 주요 재료: 🔹\n" +
                "• 신선한 파드론 페퍼\n" +
                "• 올리브 오일\n" +
                "• 소금\n\n" +
                "🔹 맛의 특징: 🔹\n" +
                "• 바삭하고 고소한 페퍼의 맛\n" +
                "• 올리브 오일의 풍부한 향과 맛\n" +
                "• 소금의 짭짤함과 페퍼의 약간의 매운 맛\n\n" +
                "🔹 추천 페어링: 🔹\n" +
                "• 시원한 맥주\n" +
                "• 가벼운 화이트 와인\n" +
                "• 청량한 스파클링 워터\n\n" +
                "이 파드론 페퍼는 친구들과 함께 즐기는 가벼운 안주로, 또는 특별한 날을 기념하는 간단한 요리로 완벽한 선택입니다. 신선한 재료와 정성스런 조리로 만들어진 파드론 페퍼를 꼭 맛보세요!",
        ingredients = listOf("신선한 파드론 페퍼", "올리브 오일", "소금")
    ),
)